/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Analytics › Screens and Analytics › Actions (contract section 5): one ranked list per kind from GET /v1/admin/usage/features
 * (name, events, users, sessions, engagement per session, a daily sparkline), searched and sorted on the client (the read
 * answers the whole window), exported as CSV with the formula-injection guard, a row opening its detail; and the names the
 * catalog (GET /v1/admin/usage/catalog) knows that this window never saw: "unused".
 */
import { useState } from 'react';
import { Download, Layers, MousePointerClick, Search } from 'lucide-react';
import { api } from '../../lib/api';
import {
  DEFAULT_FEATURE_SORT, KIND_LABELS, csvFileName, engagementPer, featuresCsv, nameLabel, normalizeCatalog, normalizeFeatures, rankFeatures, toggleFeatureSort, unusedNames,
  type FeatureKind, type FeatureRow, type FeatureSort, type FeatureSortKey,
} from '../../lib/usage';
import { Button, EmptyState, Input, Panel, Sparkline, Stack, Table, Tag, type Column } from '../../ui';
import { count } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { UsageError, downloadText, type SectionProps } from './parts';
import { FeatureDrawer } from './FeatureDrawer';

const SORT_KEYS: readonly FeatureSortKey[] = ['name', 'events', 'users', 'sessions', 'engagementMs'];
const isSortKey = (key: string): key is FeatureSortKey => (SORT_KEYS as readonly string[]).includes(key);

export function UsageFeatures({ kind, lens, query, lensKey }: SectionProps & { kind: FeatureKind }) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<FeatureSort>(DEFAULT_FEATURE_SORT);
  const [open, setOpen] = useState<FeatureRow | null>(null);
  const [exported, setExported] = useState<string | null>(null);
  const features = useLoad(() => api.usageFeatures(query).then(normalizeFeatures), `features|${lensKey}`);
  const catalog = useLoad(() => api.usageCatalog().then(normalizeCatalog), 'catalog');
  const all = features.load.state === 'ready' ? features.load.data : [];
  const rows = rankFeatures(all, kind, search, sort);
  const words = KIND_LABELS[kind];
  const icon = kind === 'screen' ? Layers : MousePointerClick;

  const exportCsv = () => {
    const text = featuresCsv(rows);
    const name = csvFileName(kind, lens);
    setExported(downloadText(name, text) ? `Exported ${name} (${count(rows.length)} rows).` : 'The browser could not save the file.');
  };

  const columns: Array<Column<FeatureRow>> = [
    { key: 'rank', header: '#', numeric: true, muted: true, width: '48px', cell: (_row, index) => count(index + 1) },
    { key: 'name', header: words.one, sortable: true, cell: (row) => <span className="ui-cell-stack"><span>{nameLabel(row.name)}</span><span className="ui-cell-sub is-mono">{row.name}</span></span> },
    { key: 'events', header: 'Events', numeric: true, sortable: true, cell: (row) => count(row.events) },
    { key: 'users', header: 'Users', numeric: true, sortable: true, cell: (row) => count(row.users) },
    { key: 'sessions', header: 'Sessions', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => count(row.sessions) },
    { key: 'engagementMs', header: 'Per session', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => engagementPer(row) },
    { key: 'spark', header: 'Per day', hideOnPhone: true, cell: (row) => <Sparkline values={row.sparkline} label={`${nameLabel(row.name)}: events per day, last ${lens.window} days`} /> },
  ];

  const unused = catalog.load.state === 'ready' ? unusedNames(catalog.load.data, all, kind, lens.hideAdmin) : [];
  const catalogEmpty = catalog.load.state === 'ready' && catalog.load.data.screens.length + catalog.load.data.actions.length === 0;

  return (
    <Stack>
      <Panel
        flush
        title={words.many}
        description={`Ranked by ${sort.key === 'name' ? 'name' : sort.key === 'engagementMs' ? 'engagement' : sort.key}, last ${lens.window} days; engagement is the time credited per session`}
        actions={(
          <>
            <Input size="sm" className="ui-w-md" aria-label={`Search ${words.many.toLowerCase()}`} placeholder={`Search ${words.many.toLowerCase()}`} prefix={<Search size={14} strokeWidth={1.75} aria-hidden="true" />} value={search} onChange={(event) => setSearch(event.target.value)} />
            <Button size="sm" icon={Download} onClick={exportCsv} disabled={features.load.state !== 'ready' || rows.length === 0} title="Download the list as shown, as CSV. Cells that a spreadsheet would read as a formula are guarded.">Export CSV</Button>
          </>
        )}
        footer={exported ? <span role="status">{exported}</span> : undefined}
      >
        {features.load.state === 'failed' ? <div className="ui-panel-pad"><UsageError title={`${words.many} could not be loaded`} error={features.load.error} onRetry={features.reload} retrying={features.reloading} /></div> : (
          <Table
            label={words.many}
            columns={columns}
            rows={rows}
            rowKey={(row) => `${row.kindRaw}|${row.name}`}
            loading={features.load.state === 'loading'}
            skeletonRows={6}
            onRowClick={setOpen}
            sort={{ key: sort.key, direction: sort.direction, onSort: (key) => { if (isSortKey(key)) setSort(toggleFeatureSort(sort, key)); } }}
            empty={search.trim()
              ? <EmptyState compact icon={Search} title={`No ${words.many.toLowerCase()} match "${search.trim()}"`}>Try fewer letters, or clear the search.</EmptyState>
              : <EmptyState compact icon={icon} title={`No ${words.many.toLowerCase()} in this window`}>{`${words.many} appear here as people use Gyro; data starts at the client release, and the lens above may be leaving people out.`}</EmptyState>}
          />
        )}
      </Panel>
      <Panel title={`Unused ${words.many.toLowerCase()}`} description={`Known to the app, never seen in the last ${lens.window} days with this lens${lens.hideAdmin ? '; the hidden admin names are not counted' : ''}`}>
        {catalog.load.state === 'failed'
          ? <UsageError title="The catalog could not be loaded" error={catalog.load.error} onRetry={catalog.reload} retrying={catalog.reloading} />
          : catalog.load.state === 'loading' || features.load.state === 'loading'
            ? <EmptyState compact title="Loading" />
            : unused.length === 0
              ? <EmptyState compact icon={icon} title={catalogEmpty ? 'The catalog is empty' : `Every known ${words.one.toLowerCase()} was used`}>{catalogEmpty ? 'The service lists no names until the catalogue and the first events reach it.' : 'Nothing in the catalog went unused in this window.'}</EmptyState>
              : <div className="ui-chips" aria-label={`Unused ${words.many.toLowerCase()}`}>{unused.map((name) => <Tag key={name}>{name}</Tag>)}</div>}
      </Panel>
      <FeatureDrawer row={open} lens={lens} query={query} onClose={() => setOpen(null)} />
    </Stack>
  );
}
